/** @format */

import * as Sentry from "@sentry/react";
import i18n from "./features/i18n/init";
import { StrictMode, type ReactNode } from "react";
import { I18nextProvider } from "react-i18next";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { AuthProvider } from "./contexts/auth/AuthProvider";
import "./index.css";
import { ToastNotification } from "./components/Toast/ToastNotification";
import { BrandProvider } from "./contexts/brand/BrandProvider";
import { AppRoutes } from "./AppRoutes";
import { initAnalytics } from "./utils/analytics";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
	Sentry.init({
		dsn: SENTRY_DSN,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "development",
		dataCollection: {
			// To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
			// https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#dataCollection
			// userInfo: false,
			// httpBodies: []
		},
	});
}

// Google Analytics is loaded only on production hosts (see initAnalytics).
initAnalytics();

const LAUNCHDARKLY_CLIENT_ID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

// eslint-disable-next-line react-refresh/only-export-components
const PassThroughProvider = ({ children }: { children: ReactNode }) => <>{children}</>;

// When no LaunchDarkly client id is configured we fall back to a pass-through
// provider so that all flags default to off (feature hidden).
async function resolveFeatureFlagProvider() {
	if (!LAUNCHDARKLY_CLIENT_ID) {
		return PassThroughProvider;
	}
	return asyncWithLDProvider({
		clientSideID: LAUNCHDARKLY_CLIENT_ID,
		context: { kind: "user", key: "anonymous", anonymous: true },
	});
}

async function bootstrap() {
	const FeatureFlagProvider = await resolveFeatureFlagProvider();
	createRoot(document.getElementById("root")!).render(
		<StrictMode>
			<FeatureFlagProvider>
				<I18nextProvider i18n={i18n}>
					<BrandProvider>
						<BrowserRouter>
							<AuthProvider>
								<ToastNotification />
								<AppRoutes />
							</AuthProvider>
						</BrowserRouter>
					</BrandProvider>
				</I18nextProvider>
			</FeatureFlagProvider>
		</StrictMode>,
	);
}

void bootstrap();
